import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: 30000, // 30 second timeout to reduce false client-side cancels during brief server bursts
});

// Helper to get proper image URL (handles /uploads -> /api/uploads for IIS proxy)
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If already absolute URL, return as-is
  if (imageUrl.startsWith('http')) return imageUrl;
  // Transform /uploads/... to /api/uploads/... for IIS proxy compatibility
  const path = imageUrl.startsWith('/uploads/')
    ? imageUrl.replace('/uploads/', '/api/uploads/')
    : imageUrl;
  return `${API_BASE}${path}`;
};

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const signup = async (formData) => {
  return apiClient.post('/api/auth/signup', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const verifyOtp = async (payload) => {
  return apiClient.post('/api/auth/verify-otp', payload);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error) => {
  const code = error?.code;
  // Axios timeout/network failures that are typically transient on unstable edge/proxy paths
  return code === 'ECONNABORTED' || code === 'ERR_NETWORK';
};

export const login = async (payload) => {
  const maxAttempts = 2;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await apiClient.post('/api/auth/login', payload, { timeout: 12000 });
    } catch (error) {
      const shouldRetry = attempt < maxAttempts && isRetryableNetworkError(error);
      if (!shouldRetry) {
        throw error;
      }
      // brief backoff before retrying once
      await sleep(800);
    }
  }
};

export const forgotPassword = async (payload) => {
  return apiClient.post('/api/auth/forgot-password', payload);
};

export const resetPassword = async (payload) => {
  return apiClient.post('/api/auth/reset-password', payload);
};

// Student APIs
export const getStudentStatus = async () => apiClient.get('/api/student/status');
export const updateStudentProfile = async (payload) => apiClient.put('/api/student/profile', payload);
export const applyGate = async (payload) => apiClient.post('/api/student/apply', payload);
export const cancelGate = async () => apiClient.post('/api/student/cancel');
export const createLocalGatepass = async (formData) =>
  apiClient.post('/api/student/local-gatepass', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
export const createOutstationGatepass = async (formData) =>
  apiClient.post('/api/student/outstation-gatepass', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
export const getStudentLogs = async () => apiClient.get('/api/student/logs');
export const getMyGatepasses = async () => apiClient.get('/api/student/my-gatepasses');
export const deleteLocalGatepass = async (gatepassId) =>
  apiClient.delete(`/api/student/local-gatepass/${gatepassId}`);
export const applyGatepassExit = async (payload) => apiClient.post('/api/student/gatepass-exit', payload);
export const applyGatepassEntry = async (payload) => apiClient.post('/api/student/gatepass-entry', payload);
export const applyOSGatepassExit = async (payload) => apiClient.post('/api/student/os-gatepass-exit', payload);
export const applyOSGatepassEntry = async (payload) => apiClient.post('/api/student/os-gatepass-entry', payload);

// Guard APIs
export const getGuardDashboard = async (options = {}) => apiClient.get('/api/guard/dashboard', options);
export const scanQrToken = async (payload) => apiClient.post('/api/guard/scan', payload);
export const decideRequest = async (payload) => apiClient.post('/api/guard/decide', payload);
export const getGuardEntryExitLogs = async ({ filter, date, search, limit } = {}) =>
  apiClient.get('/api/guard/entry-exit-logs', { params: { filter, date, search, limit } });
export const searchGuardStudents = async (q) =>
  apiClient.get('/api/guard/students', { params: { q } });
export const manualExit = async (payload) => apiClient.post('/api/guard/manual-exit', payload);
export const manualEntry = async (payload) => apiClient.post('/api/guard/manual-entry', payload);

// Admin APIs
export const getAdminProfile = async () => apiClient.get('/api/admin/profile');
export const updateAdminProfile = async (payload) => apiClient.put('/api/admin/update-profile', payload);
export const getAdminOverview = async (hostel) => apiClient.get('/api/admin/overview', { params: { hostel } });
export const getAdminLogs = async () => apiClient.get('/api/admin/logs');
export const getAdminUsers = async () => apiClient.get('/api/admin/users');
export const getAdminLiveLogs = async (hostel) => apiClient.get('/api/admin/live-logs', { params: { hostel } });
export const getStudentsInside = async (hostel) => apiClient.get('/api/admin/students-inside', { params: { hostel } });
export const getStudentsOutside = async (hostel) => apiClient.get('/api/admin/students-outside', { params: { hostel } });
export const getLocalGatepassExits = async (hostel) => apiClient.get('/api/admin/local-gatepass-exits', { params: { hostel } });
export const getOutstationGatepassExits = async (hostel) => apiClient.get('/api/admin/outstation-gatepass-exits', { params: { hostel } });
export const getSemBreakGatepassExits = async (hostel) => apiClient.get('/api/admin/sem-break-gatepass-exits', { params: { hostel } });
export const getBangaloreGatepassExits = async (hostel) => apiClient.get('/api/admin/bangalore-gatepass-exits', { params: { hostel } });
export const getDetailedLogs = async (hostel) => apiClient.get('/api/admin/detailed-logs', { params: { hostel } });
export const getAllStudents = async (hostel) => apiClient.get('/api/admin/all-students', { params: { hostel } });
export const searchAdminStudents = async (q, hostel) =>
  apiClient.get('/api/admin/search-students', { params: { q, hostel } });
export const getStudentLogsById = async (studentId, hostel) =>
  apiClient.get(`/api/admin/student-logs/${studentId}`, { params: { hostel } });
export const updateAdminStudentProfile = async (studentId, formData) =>
  apiClient.put(`/api/admin/student-profile/${studentId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
export const getAdminEntryExitLogs = async ({ filter, hostel, date, search, limit } = {}) =>
  apiClient.get('/api/admin/entry-exit-logs', { params: { filter, hostel, date, search, limit } });
export const searchGatepass = async (type, number, hostel) =>
  apiClient.get('/api/admin/search-gatepass', { params: { type, number, hostel } });
export const createAdminFaculty = async (payload) =>
  apiClient.post('/api/admin/create-faculty', payload);

// Admin Ban/Unban APIs
export const searchStudentForBan = async (rollnumber) =>
  apiClient.get('/api/admin/search-student-ban', { params: { rollnumber } });
export const banStudent = async (studentId, reason) =>
  apiClient.post('/api/admin/ban-student', { studentId, reason });
export const unbanStudent = async (studentId) =>
  apiClient.post('/api/admin/unban-student', { studentId });
export const getBannedStudents = async () =>
  apiClient.get('/api/admin/banned-students');

// Hostel Office APIs - Local Gatepass
export const getPendingGatepasses = async () => apiClient.get('/api/hostel-office/pending-gatepasses');
export const getGatepassHistory = async (search) =>
  apiClient.get('/api/hostel-office/gatepass-history', { params: { search } });
export const getEntryExitLogs = async (date, search, filter) =>
  apiClient.get('/api/hostel-office/entry-exit-logs', { params: { date, search, filter } });
export const getGatepassByNumber = async (gatePassNo) =>
  apiClient.get('/api/hostel-office/gatepass-by-number', { params: { gatePassNo } });
export const getGatepassRegisterLogs = async (fromDate, toDate, search, gatepassType) =>
  apiClient.get('/api/hostel-office/gatepass-register', { params: { fromDate, toDate, search, gatepassType } });
export const decideGatepass = async (payload) => apiClient.post('/api/hostel-office/decide-gatepass', payload);
export const getLocalStudentHistory = async (studentId) =>
  apiClient.get(`/api/hostel-office/local-student-history/${studentId}`);

// Hostel Office APIs - Outstation Gatepass
export const getHostelOfficeOSPendingGatepasses = async () =>
  apiClient.get('/api/hostel-office/os-pending-gatepasses');
export const getHostelOfficeOSGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/hostel-office/os-gatepass/${gatepassId}`);
export const getHostelOfficeOSStudentHistory = async (studentId) =>
  apiClient.get(`/api/hostel-office/os-student-history/${studentId}`);
export const getHostelOfficeOSGatepassHistory = async (search) =>
  apiClient.get('/api/hostel-office/os-gatepass-history', { params: { search } });
export const getHostelOfficeOSLogRegister = async (params) =>
  apiClient.get('/api/hostel-office/os-log-register', { params });
export const decideHostelOfficeOSGatepass = async (payload) =>
  apiClient.post('/api/hostel-office/os-decide-gatepass', payload);

// Office Secretary APIs
export const getSecretaryProfile = async () =>
  apiClient.get('/api/office-secretary/profile');
export const getSecretaryPendingGatepasses = async () =>
  apiClient.get('/api/office-secretary/pending-gatepasses');
export const getSecretaryGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/office-secretary/gatepass/${gatepassId}`);
export const getStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/office-secretary/student-history/${studentId}`);
export const getSecretaryGatepassHistory = async (search) =>
  apiClient.get('/api/office-secretary/gatepass-history', { params: { search } });
export const decideOutstationGatepass = async (payload) =>
  apiClient.post('/api/office-secretary/decide-gatepass', payload);
export const editSecretaryGatepassDetails = async (payload) =>
  apiClient.post('/api/office-secretary/edit-gatepass', payload);
export const sendSecretaryMeetingEmail = async (payload) =>
  apiClient.post('/api/office-secretary/send-meeting-email', payload);
export const updateSecretaryProfile = async (payload) =>
  apiClient.put('/api/office-secretary/update-profile', payload);

// DUGC APIs
export const getDugcProfile = async () =>
  apiClient.get('/api/dugc/profile');
export const getDugcPendingGatepasses = async () =>
  apiClient.get('/api/dugc/pending-gatepasses');
export const getDugcGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/dugc/gatepass/${gatepassId}`);
export const getDugcStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/dugc/student-history/${studentId}`);
export const getDugcGatepassHistory = async (search) =>
  apiClient.get('/api/dugc/gatepass-history', { params: { search } });
export const decideDugcGatepass = async (payload) =>
  apiClient.post('/api/dugc/decide-gatepass', payload);
export const sendDugcMeetingEmail = async (payload) =>
  apiClient.post('/api/dugc/send-meeting-email', payload);
export const updateDugcProfile = async (payload) =>
  apiClient.put('/api/dugc/update-profile', payload);

// HOD APIs
export const getHodProfile = async () =>
  apiClient.get('/api/hod/profile');
export const getHodPendingGatepasses = async () =>
  apiClient.get('/api/hod/pending-gatepasses');
export const getHodGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/hod/gatepass/${gatepassId}`);
export const getHodStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/hod/student-history/${studentId}`);
export const getHodGatepassHistory = async (search) =>
  apiClient.get('/api/hod/gatepass-history', { params: { search } });
export const decideHodGatepass = async (payload) =>
  apiClient.post('/api/hod/decide-gatepass', payload);
export const sendHodMeetingEmail = async (payload) =>
  apiClient.post('/api/hod/send-meeting-email', payload);
export const updateHodProfile = async (payload) =>
  apiClient.put('/api/hod/update-profile', payload);

// Student Outstation Gatepass APIs
export const getMyOutstationGatepasses = async () =>
  apiClient.get('/api/student/my-outstation-gatepasses');
export const deleteOutstationGatepass = async (gatepassId) =>
  apiClient.delete(`/api/student/outstation-gatepass/${gatepassId}`);

// Faculty (Instructor) APIs
export const getAllFaculties = async () =>
  apiClient.get('/api/faculty/all');
export const getFacultyProfile = async () =>
  apiClient.get('/api/faculty/profile');
export const getFacultyPendingGatepasses = async () =>
  apiClient.get('/api/faculty/pending-gatepasses');
export const getFacultyGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/faculty/gatepass/${gatepassId}`);
export const getFacultyStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/faculty/student-history/${studentId}`);
export const getFacultyGatepassHistory = async (search) =>
  apiClient.get('/api/faculty/gatepass-history', { params: { search } });
export const decideFacultyGatepass = async (payload) =>
  apiClient.post('/api/faculty/decide-gatepass', payload);
export const sendFacultyMeetingEmail = async (payload) =>
  apiClient.post('/api/faculty/send-meeting-email', payload);
export const updateFacultyProfile = async (payload) =>
  apiClient.put('/api/faculty/update-profile', payload);

// DPGC APIs
export const getDpgcProfile = async () =>
  apiClient.get('/api/dpgc/profile');
export const getDpgcPendingGatepasses = async () =>
  apiClient.get('/api/dpgc/pending-gatepasses');
export const getDpgcGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/dpgc/gatepass/${gatepassId}`);
export const getDpgcStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/dpgc/student-history/${studentId}`);
export const getDpgcGatepassHistory = async (search) =>
  apiClient.get('/api/dpgc/gatepass-history', { params: { search } });
export const decideDpgcGatepass = async (payload) =>
  apiClient.post('/api/dpgc/decide-gatepass', payload);
export const sendDpgcMeetingEmail = async (payload) =>
  apiClient.post('/api/dpgc/send-meeting-email', payload);
export const updateDpgcProfile = async (payload) =>
  apiClient.put('/api/dpgc/update-profile', payload);

// Dean APIs
export const getDeanProfile = async () =>
  apiClient.get('/api/dean/profile');
export const getDeanPendingGatepasses = async () =>
  apiClient.get('/api/dean/pending-gatepasses');
export const getDeanGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/dean/gatepass/${gatepassId}`);
export const getDeanStudentOSHistory = async (studentId) =>
  apiClient.get(`/api/dean/student-history/${studentId}`);
export const getDeanGatepassHistory = async (search) =>
  apiClient.get('/api/dean/gatepass-history', { params: { search } });
export const decideDeanGatepass = async (payload) =>
  apiClient.post('/api/dean/decide-gatepass', payload);
export const sendDeanMeetingEmail = async (payload) =>
  apiClient.post('/api/dean/send-meeting-email', payload);
export const updateDeanProfile = async (payload) =>
  apiClient.put('/api/dean/update-profile', payload);

// Mid/End Sem Break Gatepass APIs (Student)
export const createMidSemBreakGatepass = async (formData) =>
  apiClient.post('/api/student/midsem-break-gatepass', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
export const getMyMidSemBreakGatepasses = async () =>
  apiClient.get('/api/student/my-midsem-break-gatepasses');
export const deleteMidSemBreakGatepass = async (gatepassId) =>
  apiClient.delete(`/api/student/midsem-break-gatepass/${gatepassId}`);

// Hostel Office APIs - Mid/End Sem Break Gatepass
export const getHostelOfficeSBPendingGatepasses = async () =>
  apiClient.get('/api/hostel-office/sb-pending-gatepasses');
export const getHostelOfficeSBGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/hostel-office/sb-gatepass/${gatepassId}`);
export const getHostelOfficeSBStudentHistory = async (studentId) =>
  apiClient.get(`/api/hostel-office/sb-student-history/${studentId}`);
export const getHostelOfficeSBGatepassHistory = async (search) =>
  apiClient.get('/api/hostel-office/sb-gatepass-history', { params: { search } });
export const getHostelOfficeSBLogRegister = async (params) =>
  apiClient.get('/api/hostel-office/sb-log-register', { params });
export const decideHostelOfficeSBGatepass = async (payload) =>
  apiClient.post('/api/hostel-office/sb-decide-gatepass', payload);

// Bangalore Gatepass APIs (Student)
export const createBangaloreGatepass = async (payload) =>
  apiClient.post('/api/student/bangalore-gatepass', payload);
export const getMyBangaloreGatepasses = async () =>
  apiClient.get('/api/student/my-bangalore-gatepasses');
export const deleteBangaloreGatepass = async (gatepassId) =>
  apiClient.delete(`/api/student/bangalore-gatepass/${gatepassId}`);
export const applyBangaloreGatepassExit = async (payload) =>
  apiClient.post('/api/student/bangalore-gatepass-exit', payload);

// Bangalore Gatepass APIs (Hostel Office)
export const getHostelOfficeBGPendingGatepasses = async () =>
  apiClient.get('/api/hostel-office/bg-pending-gatepasses');
export const getHostelOfficeBGGatepassDetails = async (gatepassId) =>
  apiClient.get(`/api/hostel-office/bg-gatepass/${gatepassId}`);
export const decideHostelOfficeBGGatepass = async (payload) =>
  apiClient.post('/api/hostel-office/bg-decide-gatepass', payload);
export const getHostelOfficeBGLogRegister = async (params) =>
  apiClient.get('/api/hostel-office/bg-log-register', { params });
export const revertBangaloreStudent = async (studentId) =>
  apiClient.post(`/api/hostel-office/bg-revert/${studentId}`);

// Bangalore Gatepass APIs (Admin/Warden read-only)
export const getAdminBGLogRegister = async (params) =>
  apiClient.get('/api/admin/bg-log-register', { params });
export const adminRevertBangaloreStudent = async (studentId) =>
  apiClient.post(`/api/admin/bg-revert/${studentId}`);

export default apiClient;
